/** Specialty commercial service landings — unique intent, not duplicates of pillar pages */

import type { Service } from "@/lib/data/services";

export const specialtyServices: Service[] = [
  {
    id: "gen-ai",
    number: "13",
    title: "Generative AI Development",
    slug: "generative-ai-development",
    href: "/services/generative-ai-development",
    short: "Production generative AI systems grounded in your data, workflows, and governance.",
    description:
      "Design and ship generative AI applications — copilots, content systems, and LLM products — with evaluation, security, and a clear path from pilot to production.",
    seoTitle: "Generative AI Development Company",
    seoDescription:
      "MaxRidge is a generative AI development company building LLM apps, copilots, and enterprise GenAI systems with grounding, evaluation, and production operations.",
    keywords: [
      "generative AI development company",
      "generative AI development",
      "enterprise generative AI",
      "GenAI development services",
      "LLM product development",
    ],
    capabilities: [
      "LLM application design",
      "Prompt & workflow engineering",
      "Grounded generation (RAG)",
      "Evaluation & safety rails",
      "Human-in-the-loop UX",
      "Multi-model strategy",
      "Cost & latency optimization",
      "Production observability",
    ],
    accent: "from-violet-500/30 to-fuchsia-400/15",
    featured: false,
    problem: {
      title: "GenAI demos that never become products.",
      body: "Many teams ship chat prototypes without grounding, evaluation, permissions, or ownership — so quality drifts and stakeholders lose trust.",
    },
    opportunity: {
      title: "Generative systems that operators can rely on.",
      body: "We engineer GenAI into real workflows: grounded answers, measurable quality, escalation paths, and cloud ops that finance and security can accept.",
    },
    outcomes: [
      "Faster knowledge and content workflows",
      "Higher trust through grounding and citations",
      "Clear evaluation loops before wider rollout",
      "Controlled cost and model usage",
    ],
    faqs: [
      {
        question: "Do you build custom GenAI or only wrap ChatGPT?",
        answer:
          "We design product experiences around the right models for the job — including OpenAI, Anthropic, Gemini, and open models — with your data, tools, and brand constraints.",
      },
      {
        question: "How do you reduce hallucinations?",
        answer:
          "Grounding with retrieval, constrained generation, tool use, refusal behavior, and continuous evaluation against real user questions.",
      },
      {
        question: "Can you start with a focused pilot?",
        answer:
          "Yes. We typically prove one high-ROI workflow with success metrics, then harden for production and expand.",
      },
    ],
  },
  {
    id: "ai-agents",
    number: "14",
    title: "AI Agent Development",
    slug: "ai-agent-development",
    href: "/services/ai-agent-development",
    short: "Tool-using AI agents that execute multi-step work with guardrails and observability.",
    description:
      "Build agentic systems that plan, call tools, and complete business workflows — with permissions, audit trails, and human approval where risk is high.",
    seoTitle: "AI Agent Development Company",
    seoDescription:
      "AI agent development company MaxRidge designs production agentic workflows — multi-step automation, tool use, guardrails, and observability for enterprise operations.",
    keywords: [
      "AI agent development company",
      "agentic AI development",
      "autonomous AI agents",
      "multi-agent systems",
      "AI workflow automation",
    ],
    capabilities: [
      "Agent architecture & orchestration",
      "Tool / API integration",
      "Multi-agent collaboration",
      "Approval gates for risky actions",
      "Memory & state design",
      "Evaluation of agent runs",
      "Security & least privilege",
      "Ops dashboards & tracing",
    ],
    accent: "from-cyan-400/25 to-violet-500/20",
    featured: false,
    problem: {
      title: "Automation stops where judgment and tools begin.",
      body: "RPA and scripts break on exceptions. Chatbots answer but cannot reliably act across systems.",
    },
    opportunity: {
      title: "Agents that work inside your operating model.",
      body: "We scope tools carefully, define escalation, and ship agents that reduce manual coordination without surprising your security team.",
    },
    outcomes: [
      "Fewer multi-system handoffs",
      "Shorter cycle time on repetitive processes",
      "Visible agent runs and failure handling",
      "Safer expansion of autonomy over time",
    ],
    faqs: [
      {
        question: "How autonomous should an agent be on day one?",
        answer:
          "Usually limited: read and draft first, then write actions behind approvals. Autonomy expands after evaluation proves reliability.",
      },
      {
        question: "Do you use LangGraph or custom orchestration?",
        answer:
          "We pick the lightest durable approach — frameworks like LangGraph when helpful, or custom orchestrators when control and audit matter more.",
      },
    ],
  },
  {
    id: "llm-dev",
    number: "15",
    title: "LLM Development",
    slug: "llm-development",
    href: "/services/llm-development",
    short: "LLM application engineering — APIs, product UX, evaluation, and model operations.",
    description:
      "End-to-end LLM development for products and internal tools: architecture, prompt systems, retrieval, fine-tuning when justified, and production hardening.",
    seoTitle: "LLM Development Company",
    seoDescription:
      "LLM development company MaxRidge builds large language model applications — product UX, RAG, evaluation, fine-tuning guidance, and cloud operations for reliable AI features.",
    keywords: [
      "LLM development company",
      "LLM application development",
      "large language model development",
      "LLM engineering services",
      "fine-tuning consulting",
    ],
    capabilities: [
      "LLM product architecture",
      "Prompt systems & tools",
      "RAG pipelines",
      "Fine-tuning decisioning",
      "Eval harnesses",
      "Streaming UX",
      "Rate limits & cost controls",
      "Model gateway patterns",
    ],
    accent: "from-blue-500/25 to-cyan-400/20",
    featured: false,
    problem: {
      title: "LLM features that feel clever but break under real use.",
      body: "Latency, cost, prompt drift, and weak eval make LLM features unreliable after the demo.",
    },
    opportunity: {
      title: "LLM capabilities engineered like software products.",
      body: "We treat models as components in a system — with contracts, tests, monitoring, and UX that sets the right expectations.",
    },
    outcomes: [
      "Stable LLM features in production apps",
      "Measurable answer/task quality",
      "Predictable spend patterns",
      "Clear when to retrieve vs fine-tune",
    ],
    faqs: [
      {
        question: "When do you recommend fine-tuning?",
        answer:
          "When style, format, or domain behavior cannot be achieved reliably with prompting and retrieval — and you have enough quality data to justify the cost.",
      },
      {
        question: "Can you work with private / VPC-hosted models?",
        answer:
          "Yes. We support cloud APIs and private deployments depending on latency, compliance, and cost constraints.",
      },
    ],
  },
  {
    id: "rag-dev",
    number: "16",
    title: "RAG Development",
    slug: "rag-development",
    href: "/services/rag-development",
    short: "Enterprise RAG platforms with permissions, hybrid search, citations, and evaluation.",
    description:
      "Retrieval-augmented generation that leadership can trust — chunking strategies, hybrid search, access control, citations, and continuous quality measurement.",
    seoTitle: "RAG Development Company",
    seoDescription:
      "RAG development company MaxRidge builds enterprise retrieval-augmented generation systems — vector search, permissions, citations, and evaluation for grounded AI answers.",
    keywords: [
      "RAG development company",
      "enterprise RAG development",
      "retrieval augmented generation services",
      "vector search development",
      "knowledge base AI",
    ],
    capabilities: [
      "Document ingestion pipelines",
      "Chunking & metadata design",
      "Hybrid keyword + vector search",
      "Permission-aware retrieval",
      "Citation UX",
      "Eval sets & regression tests",
      "Re-ranking strategies",
      "Ops for index freshness",
    ],
    accent: "from-emerald-400/25 to-cyan-400/20",
    featured: false,
    problem: {
      title: "Knowledge is scattered; answers are untrusted.",
      body: "Policies, tickets, and product docs live in silos. Generic chatbots invent answers when retrieval is weak.",
    },
    opportunity: {
      title: "Grounded answers with sources people can verify.",
      body: "We build RAG as a platform capability — not a one-off chatbot — so multiple products can share reliable retrieval.",
    },
    outcomes: [
      "Faster access to approved knowledge",
      "Answers tied to source documents",
      "Fewer hallucinated policy responses",
      "A foundation for copilots and agents",
    ],
    faqs: [
      {
        question: "Which vector databases do you use?",
        answer:
          "We select based on your cloud and scale needs — including managed vector stores and search platforms already in your stack.",
      },
      {
        question: "How do you handle document permissions?",
        answer:
          "Retrieval respects source ACLs or mapped roles so users only see answers grounded in content they are allowed to access.",
      },
    ],
  },
  {
    id: "ml-dev",
    number: "17",
    title: "Machine Learning Development",
    slug: "machine-learning-development",
    href: "/services/machine-learning-development",
    short: "Classical and modern ML systems connected to products, data, and MLOps.",
    description:
      "Machine learning development for forecasting, ranking, risk, and personalization — from feature pipelines to model monitoring in production.",
    seoTitle: "Machine Learning Development Company",
    seoDescription:
      "Machine learning development company MaxRidge builds predictive models, ranking systems, and MLOps pipelines connected to real business workflows.",
    keywords: [
      "machine learning development company",
      "ML development services",
      "predictive modeling company",
      "MLOps services",
      "custom ML model development",
    ],
    capabilities: [
      "Problem framing & baselines",
      "Feature engineering",
      "Model training & selection",
      "Offline & online evaluation",
      "MLOps & model registry",
      "Drift monitoring",
      "Decisioning UX",
      "Cloud ML platforms",
    ],
    accent: "from-amber-400/20 to-orange-400/15",
    featured: false,
    problem: {
      title: "Models that never reach the operator.",
      body: "Notebooks and accuracy metrics do not create value until predictions drive decisions in software people use daily.",
    },
    opportunity: {
      title: "ML embedded in products and operations.",
      body: "We ship the full path — data, model, API, UI, and monitoring — so predictions stay useful after launch.",
    },
    outcomes: [
      "Earlier signals for demand, risk, or quality",
      "Models tied to concrete decisions",
      "Monitoring for drift and failures",
      "A repeatable path to improve models",
    ],
    faqs: [
      {
        question: "Is generative AI always the right approach?",
        answer:
          "No. Many forecasting, ranking, and classification problems are better served by classical or specialized ML — we choose based on the decision, data, and latency needs.",
      },
    ],
  },
  {
    id: "cv-dev",
    number: "18",
    title: "Computer Vision Development",
    slug: "computer-vision-development",
    href: "/services/computer-vision-development",
    short: "Vision systems for inspection, document understanding, and visual intelligence.",
    description:
      "Computer vision development for quality inspection, document OCR pipelines, media understanding, and vision models integrated into enterprise workflows.",
    seoTitle: "Computer Vision Development Company",
    seoDescription:
      "Computer vision development services from MaxRidge — inspection, OCR/document AI, and visual ML systems integrated with your operations and cloud stack.",
    keywords: [
      "computer vision development company",
      "computer vision services",
      "document AI development",
      "visual inspection AI",
      "OCR pipeline development",
    ],
    capabilities: [
      "Detection & classification",
      "Document / OCR pipelines",
      "Quality inspection workflows",
      "Dataset & labeling strategy",
      "Edge vs cloud inference",
      "Human review loops",
      "Model evaluation",
      "API & dashboard delivery",
    ],
    accent: "from-sky-400/25 to-indigo-400/15",
    featured: false,
    problem: {
      title: "Visual work that does not scale with people alone.",
      body: "Manual inspection and document processing create bottlenecks and inconsistent judgments.",
    },
    opportunity: {
      title: "Vision systems with humans in control.",
      body: "We automate the repetitive visual load and keep experts focused on exceptions that need judgment.",
    },
    outcomes: [
      "Higher throughput on visual tasks",
      "More consistent classifications",
      "Clear escalation for uncertain cases",
      "Integration into existing ops tools",
    ],
    faqs: [
      {
        question: "Do you need labeled data to start?",
        answer:
          "Often we start with a seed set and active learning. For some document tasks, strong OCR plus rules and LLMs can reduce labeling needs.",
      },
    ],
  },
  {
    id: "nlp-dev",
    number: "19",
    title: "NLP Development",
    slug: "nlp-development",
    href: "/services/nlp-development",
    short: "Natural language processing for classification, extraction, search, and assistants.",
    description:
      "NLP development spanning classical NLP and modern language models — entity extraction, classification, semantic search, and text automation for business systems.",
    seoTitle: "NLP Development Company",
    seoDescription:
      "NLP development company MaxRidge builds text classification, extraction, semantic search, and language understanding systems for enterprise products.",
    keywords: [
      "NLP development company",
      "natural language processing services",
      "text classification development",
      "entity extraction NLP",
      "semantic search development",
    ],
    capabilities: [
      "Classification & routing",
      "Entity & relation extraction",
      "Semantic search",
      "Summarization pipelines",
      "Intent detection",
      "Multilingual text handling",
      "PII-aware processing",
      "Evaluation datasets",
    ],
    accent: "from-indigo-400/25 to-cyan-400/15",
    featured: false,
    problem: {
      title: "Unstructured text is where work hides.",
      body: "Tickets, emails, contracts, and notes contain decisions — but teams still process them manually.",
    },
    opportunity: {
      title: "Language systems that reduce queue time.",
      body: "We turn text into structured signals and assisted workflows with measurable precision and recall.",
    },
    outcomes: [
      "Faster triage and routing",
      "Structured data from free text",
      "Better search over knowledge corpora",
      "Assistants grounded in domain language",
    ],
    faqs: [
      {
        question: "When do you use classical NLP vs LLMs?",
        answer:
          "Classical or specialized models often win for high-volume classification with strict latency/cost. LLMs excel at flexible understanding and generation — we mix both.",
      },
    ],
  },
  {
    id: "ai-consult",
    number: "20",
    title: "AI Consulting",
    slug: "ai-consulting",
    href: "/services/ai-consulting",
    short: "AI strategy, use-case prioritization, architecture, and production readiness reviews.",
    description:
      "AI consulting for leaders who need clarity: which use cases to fund, how to architecture platforms, how to govern models, and how to move from pilots to durable systems.",
    seoTitle: "AI Consulting Company",
    seoDescription:
      "AI consulting company MaxRidge helps enterprises prioritize AI use cases, design platforms, set governance, and plan production delivery — not slideware.",
    keywords: [
      "AI consulting company",
      "AI consulting services",
      "enterprise AI strategy",
      "generative AI consulting",
      "AI readiness assessment",
    ],
    capabilities: [
      "AI opportunity assessment",
      "Use-case prioritization",
      "Architecture blueprints",
      "Build vs buy guidance",
      "Governance & risk framing",
      "Vendor / model selection",
      "Delivery roadmaps",
      "Team capability planning",
    ],
    accent: "from-violet-400/20 to-slate-400/10",
    featured: false,
    problem: {
      title: "AI budgets without a production thesis.",
      body: "Pilots multiply, vendors pile up, and none of it compounds into a platform or measurable operating advantage.",
    },
    opportunity: {
      title: "A sequenced plan leaders can fund.",
      body: "We connect business outcomes to technical architecture and a realistic delivery model with MaxRidge engineering when you are ready to build.",
    },
    outcomes: [
      "Clear prioritized backlog of AI bets",
      "Architecture that avoids dead ends",
      "Shared language for risk and ROI",
      "A path from advisory into build",
    ],
    faqs: [
      {
        question: "Is consulting separate from implementation?",
        answer:
          "You can engage us for advisory only, or continue into design and build with the same team for continuity.",
      },
      {
        question: "Do you work with our internal AI team?",
        answer:
          "Yes. We often augment strategy and architecture alongside in-house teams rather than replace them.",
      },
    ],
  },
  {
    id: "api-dev",
    number: "21",
    title: "API Development",
    slug: "api-development",
    href: "/services/api-development",
    short: "Secure, well-documented APIs and integration layers for products and partners.",
    description:
      "API development for modern products — REST and event-driven interfaces, authentication, versioning, documentation, and reliability for partners and internal consumers.",
    seoTitle: "API Development Company",
    seoDescription:
      "API development services from MaxRidge — REST and event APIs, authentication, versioning, and integration layers for SaaS products and enterprise systems.",
    keywords: [
      "API development company",
      "REST API development",
      "API integration services",
      "backend API development",
      "API design services",
    ],
    capabilities: [
      "API design & contracts",
      "AuthN / AuthZ",
      "Rate limiting & quotas",
      "Versioning strategies",
      "OpenAPI documentation",
      "Webhooks & events",
      "Observability",
      "Partner developer experience",
    ],
    accent: "from-teal-400/20 to-cyan-400/15",
    featured: false,
    problem: {
      title: "Integrations become the product bottleneck.",
      body: "Without clear APIs, every partner and internal team invents brittle connections.",
    },
    opportunity: {
      title: "APIs as a durable product surface.",
      body: "We design interfaces that products, mobile apps, and AI agents can safely reuse.",
    },
    outcomes: [
      "Faster partner and app integrations",
      "Fewer breaking changes",
      "Clear ownership of contracts",
      "Safer foundation for automation",
    ],
    faqs: [
      {
        question: "Do you modernize legacy SOAP or proprietary APIs?",
        answer:
          "Yes. We often wrap legacy systems with modern API façades while planning deeper modernization.",
      },
    ],
  },
  {
    id: "microservices",
    number: "22",
    title: "Microservices Development",
    slug: "microservices-development",
    href: "/services/microservices-development",
    short: "Modular service architectures with clear boundaries, observability, and delivery.",
    description:
      "Microservices and modular monolith guidance — split where it pays off, keep cohesion where it does not, and ship with CI/CD, contracts, and ops maturity.",
    seoTitle: "Microservices Development Company",
    seoDescription:
      "Microservices development company MaxRidge designs modular backends — service boundaries, APIs, events, Kubernetes-ready delivery, and observability.",
    keywords: [
      "microservices development company",
      "microservices architecture services",
      "modular monolith development",
      "distributed systems development",
      "Kubernetes microservices",
    ],
    capabilities: [
      "Domain boundary design",
      "Service templates",
      "Event-driven patterns",
      "Contract testing",
      "Containerization",
      "Kubernetes delivery",
      "Distributed tracing",
      "Resilience patterns",
    ],
    accent: "from-slate-400/20 to-blue-400/15",
    featured: false,
    problem: {
      title: "Monoliths that slow every change — or microservices chaos.",
      body: "Teams either fear deploying a giant codebase or drown in distributed complexity without clear ownership.",
    },
    opportunity: {
      title: "Modularity matched to team and domain reality.",
      body: "We choose modular monoliths or microservices based on scale, team topology, and change frequency — not fashion.",
    },
    outcomes: [
      "Independent deployability where it matters",
      "Clearer ownership of domains",
      "Better failure isolation",
      "A migration path without big-bang rewrites",
    ],
    faqs: [
      {
        question: "Do you always recommend microservices?",
        answer:
          "No. Many products benefit from a well-structured modular monolith first. We introduce distribution when team and scale needs justify it.",
      },
    ],
  },
  {
    id: "outsourcing",
    number: "23",
    title: "Software Development Outsourcing",
    slug: "software-development-outsourcing",
    href: "/services/software-development-outsourcing",
    short: "Outsource software delivery to an AI-first engineering partner in India.",
    description:
      "Software development outsourcing with MaxRidge — product engineering, AI features, and platform work delivered from Noida with transparent rituals and ownership.",
    seoTitle: "Software Development Outsourcing Company India",
    seoDescription:
      "IT outsourcing company in India — MaxRidge delivers software development outsourcing for custom products, AI systems, and dedicated engineering capacity from Noida.",
    keywords: [
      "software development outsourcing",
      "IT outsourcing company India",
      "outsource software development to India",
      "offshore software development company",
      "software outsourcing partner",
    ],
    capabilities: [
      "Product engineering pods",
      "AI & data workstreams",
      "Discovery & roadmap support",
      "QA & release discipline",
      "Time-zone friendly collaboration",
      "IP & security practices",
      "Flexible engagement models",
      "Knowledge transfer",
    ],
    accent: "from-accent/25 to-cyan/15",
    featured: false,
    problem: {
      title: "Outsourcing that becomes a black box.",
      body: "Low-context vendors ship tickets without product judgment, leaving you with brittle code and surprise delays.",
    },
    opportunity: {
      title: "A partner that thinks with you.",
      body: "We operate as an extension of your team — clear communication, engineering standards, and AI-first capability when you need it.",
    },
    outcomes: [
      "Predictable delivery cadence",
      "Access to AI and full-stack skills",
      "Lower coordination overhead",
      "Code and documentation you can own",
    ],
    faqs: [
      {
        question: "Where is the MaxRidge team based?",
        answer:
          "Our primary delivery hub is Sector 63, Noida (Delhi NCR), India, collaborating with clients worldwide.",
      },
      {
        question: "How do you handle IP and confidentiality?",
        answer:
          "We work under NDAs and contractual IP assignment, with access controls appropriate to your engagement.",
      },
    ],
  },
  {
    id: "staff-aug",
    number: "24",
    title: "Staff Augmentation",
    slug: "staff-augmentation",
    href: "/services/staff-augmentation",
    short: "Embed skilled engineers into your team with clear ownership and ramp plans.",
    description:
      "Staff augmentation for product and platform teams — React, Node, Python, Java, AI, cloud, and data engineers who join your rituals and tools.",
    seoTitle: "IT Staff Augmentation Services India",
    seoDescription:
      "Staff augmentation services from MaxRidge — hire skilled developers in India to extend your team for AI, web, mobile, cloud, and data engineering work.",
    keywords: [
      "staff augmentation services",
      "IT staff augmentation India",
      "hire developers India",
      "software staff augmentation",
      "team augmentation services",
    ],
    capabilities: [
      "Role-based talent matching",
      "Fast onboarding into your stack",
      "Shared sprint rituals",
      "Senior oversight options",
      "Flexible ramp up / down",
      "AI & full-stack profiles",
      "Timezone overlap planning",
      "Performance transparency",
    ],
    accent: "from-cyan/20 to-accent/15",
    featured: false,
    problem: {
      title: "Hiring is too slow for the roadmap.",
      body: "Critical features wait while recruiting cycles drag — or contractors arrive without context and create more debt.",
    },
    opportunity: {
      title: "Capacity that feels like your team.",
      body: "We place engineers who adopt your codebase, ceremonies, and quality bar — with MaxRidge support behind them.",
    },
    outcomes: [
      "Faster capacity when priorities spike",
      "Skills matched to your stack",
      "Less recruiting overhead",
      "Option to convert to dedicated pods",
    ],
    faqs: [
      {
        question: "How is this different from a dedicated team?",
        answer:
          "Staff augmentation embeds people into your management structure. Dedicated teams often own a backlog end-to-end with MaxRidge delivery leadership.",
      },
      {
        question: "What profiles can you provide?",
        answer:
          "Common requests include React/Next.js, Node.js, Python, Java/Spring, AI/ML, data engineering, and DevOps — based on availability and fit.",
      },
    ],
  },
  {
    id: "aws",
    number: "25",
    title: "AWS Consulting",
    slug: "aws-consulting",
    href: "/services/aws-consulting",
    short: "AWS architecture, migration, and cloud-native delivery for products and AI workloads.",
    description:
      "AWS consulting and engineering — landing zones, migrations, containers, data platforms, and AI workloads designed for reliability and cost control.",
    seoTitle: "AWS Consulting & Development Company",
    seoDescription:
      "AWS consulting services from MaxRidge — cloud architecture, migration, Kubernetes, data, and AI workloads on Amazon Web Services.",
    keywords: [
      "AWS consulting company",
      "AWS development services",
      "AWS migration consulting",
      "AWS cloud architecture",
      "AWS DevOps services",
    ],
    capabilities: [
      "Landing zone & IAM design",
      "Migration planning",
      "ECS / EKS workloads",
      "Serverless patterns",
      "Data & ML on AWS",
      "CI/CD on AWS",
      "Cost & observability",
      "Security baselines",
    ],
    accent: "from-orange-400/25 to-amber-300/15",
    featured: false,
    problem: {
      title: "AWS sprawl without a clear operating model.",
      body: "Accounts, services, and costs grow faster than architecture and ownership.",
    },
    opportunity: {
      title: "AWS platforms teams can actually run.",
      body: "We align architecture with delivery pipelines so product teams ship safely on AWS.",
    },
    outcomes: [
      "Clearer cloud foundations",
      "Safer deployments",
      "Better cost visibility",
      "Ready path for AI and data workloads",
    ],
    faqs: [
      {
        question: "Do you only advise or also implement?",
        answer:
          "Both. Consulting engagements can continue into hands-on implementation with our cloud engineers.",
      },
    ],
  },
  {
    id: "azure",
    number: "26",
    title: "Azure Consulting",
    slug: "azure-consulting",
    href: "/services/azure-consulting",
    short: "Azure architecture and engineering for enterprise apps, data, and AI.",
    description:
      "Azure consulting for enterprises — identity-aware apps, AKS, data platforms, and Azure OpenAI patterns with governance suited to regulated environments.",
    seoTitle: "Azure Consulting & Development Company",
    seoDescription:
      "Azure consulting services from MaxRidge — enterprise applications, AKS, data platforms, and Azure AI workloads with security and governance in mind.",
    keywords: [
      "Azure consulting company",
      "Azure development services",
      "Azure OpenAI consulting",
      "AKS consulting",
      "Microsoft Azure migration",
    ],
    capabilities: [
      "Azure landing zones",
      "Entra ID–aware apps",
      "AKS & App Service",
      "Azure data services",
      "Azure OpenAI patterns",
      "DevOps on Azure",
      "Governance & tagging",
      "Hybrid connectivity",
    ],
    accent: "from-blue-500/25 to-sky-400/15",
    featured: false,
    problem: {
      title: "Enterprise Azure needs more than lift-and-shift.",
      body: "Identity, networking, and compliance constraints block naive cloud patterns — especially for AI.",
    },
    opportunity: {
      title: "Azure systems that fit how enterprises already work.",
      body: "We design with Entra ID, policy, and operational realities so AI and apps can go live.",
    },
    outcomes: [
      "Architecture aligned to enterprise identity",
      "Production-ready AI and app platforms",
      "Clearer governance",
      "Faster delivery on Azure",
    ],
    faqs: [
      {
        question: "Do you implement Azure OpenAI solutions?",
        answer:
          "Yes — including grounded apps, access control, and operational patterns appropriate for enterprise Azure estates.",
      },
    ],
  },
];
